import dayjs from 'dayjs'

import Icon from '@components/utils/Icon'
import Badge from '@atoms/Badge'
import { Button } from '@atoms'
import Link from '@atoms/Link'
import { Stat } from '@atoms/Stat'
import { IconText } from '@atoms/IconText'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@atoms/Tooltip'
import { CopiableValue } from '@molecules/CopiableValue'
import { SupporterTagsList } from '@molecules/SupporterTagsList'
import { ModalLink } from '@inertiaui/modal-react'
import { scopedTranslation } from '@utils/I18n'
import { formatDate } from '@utils/formatDate'
import type { GroupMembersSearchQueryType } from '@frontend/graphql/queries/groups'
import { supporterProfileUrl } from '../../utils/routes'
import { useDialog } from '@components/DialogProvider'

const t = scopedTranslation('components')
const tSupporterShow = scopedTranslation('supporter_show')
const tShared = scopedTranslation('shared')

type SupporterEdgeType = GroupMembersSearchQueryType['group']['supporters']['edges'][0]

type SupporterCardType = {
  groupId: string
  membership: SupporterEdgeType['membership']
  node: SupporterEdgeType['node']
  dataCompId?: string
  canSendIndividualSms?: boolean
  canMakePhoneCalls?: boolean
  demoSite?: boolean
  electoralFiltersEnabled?: boolean
  showAllTags?: boolean
  showBirthdate?: boolean
}

export function GroupShowSupporterCard({
  groupId,
  node: { id, firstName, lastName, engagementLevel, lastContactedAt, email, primaryMobile, location, federalDistrict, birthdate, rsvpCount, note, tags },
  membership,
  dataCompId,
  canSendIndividualSms = true,
  canMakePhoneCalls = true,
  demoSite = false,
  electoralFiltersEnabled = false,
  showAllTags = false,
  showBirthdate = false,
}: SupporterCardType) {
  const { open: openDialog } = useDialog()
  const supporterUrl = supporterProfileUrl(groupId, id)
  const emptyField = t('supporter_card.empty_field')

  const joinedGroup = membership?.createdAt ? formatDate(membership.createdAt) : null
  const lastContactedDate = lastContactedAt ? formatDate(lastContactedAt) : null
  const daysSinceContact = lastContactedAt ? dayjs().diff(dayjs(lastContactedAt), 'day') : null
  const formattedBirthdate = birthdate ? formatDate(birthdate) : null
  const showFederalDistrict = electoralFiltersEnabled && Boolean(federalDistrict)
  const tagNodes = tags?.nodes ?? []
  const smsDisabledReason = demoSite ? tShared('demo_site.sending_disabled') : t('supporter_card.actions.send_sms_disabled')
  const callDisabledReason = demoSite ? tShared('demo_site.calls_disabled') : t('supporter_card.actions.call_disabled')

  return (
    <div
      role="listitem"
      data-testid={`supporter-card-${id}`}
      {...(dataCompId && { 'data-compid': dataCompId })}
      className="tw:flex tw:flex-col tw:gap-4 tw:bg-white tw:w-full tw:p-4 tw:sm:p-6 tw:border tw:border-gray-200 tw:rounded-xl tw:shadow-xs"
    >
      <div className="tw:flex tw:flex-wrap tw:justify-between tw:items-start tw:gap-x-4 tw:gap-y-2 tw:pb-2">
        <h2 className="tw:flex tw:items-center tw:gap-2 tw:text-gray-900 tw:font-semibold tw:text-xl tw:w-fit tw:hover:text-primary-700">
          <Icon type="users-01" width={24} height={24} className="tw:text-primary-600" />
          <ModalLink href={supporterUrl} navigate aria-describedby={`supporter-open-${id}`}>
            {firstName} {lastName}
          </ModalLink>
        </h2>
        {daysSinceContact !== null && (
          <Badge shape="rounded" bordered color="info" leadingIcon="clock" data-testid="days-since-contact">
            {t('supporter_card.days_since_contact', { count: daysSinceContact })}
          </Badge>
        )}
      </div>

      {(primaryMobile || email) && (
        <div className="tw:flex tw:flex-wrap tw:gap-x-6 tw:gap-y-1 tw:text-sm tw:text-gray-700 tw:break-all">
          {primaryMobile && (
            <CopiableValue icon="phone-call-01" value={primaryMobile} copyLabel={t('supporter_card.copy_phone')} testId={`phone-${id}`} />
          )}
          {email && (
            <CopiableValue icon="mail-01" value={email} copyLabel={t('supporter_card.copy_email')} testId={`email-${id}`} />
          )}
        </div>
      )}

      {(location || showFederalDistrict) && (
        <TooltipProvider>
          <div className="tw:flex tw:flex-wrap tw:gap-x-6 tw:gap-y-1 tw:text-sm tw:text-gray-900">
            {location && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconText
                    icon="location"
                    data-testid="supporter-location"
                    aria-label={`${t('supporter_card.location')}: ${location}`}
                  >
                    {location}
                  </IconText>
                </TooltipTrigger>
                <TooltipContent>{t('supporter_card.location')}</TooltipContent>
              </Tooltip>
            )}
            {showFederalDistrict && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconText
                    icon="bank"
                    data-testid="federal-district"
                    className="tw:hidden tw:sm:inline-flex"
                    aria-label={`${tSupporterShow('federal_districts.title')}: ${federalDistrict}`}
                  >
                    {federalDistrict}
                  </IconText>
                </TooltipTrigger>
                <TooltipContent>{tSupporterShow('federal_districts.title')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      )}

      <div className="tw:border-t tw:border-gray-200 tw:w-full" />

      <dl className="tw:grid tw:grid-cols-2 tw:gap-x-6 tw:gap-y-4 tw:sm:grid-cols-3 tw:lg:flex tw:lg:flex-wrap tw:lg:justify-between tw:m-0">
        <Stat
          icon="activity"
          label={tSupporterShow('engagement_level')}
          value={engagementLevel}
          fallbackValue={t('supporter_card.none')}
          testId="engagement-level"
        />
        {showBirthdate && (
          <Stat
            icon="calendar-heart-01"
            label={tSupporterShow('birthdate')}
            value={formattedBirthdate}
            fallbackValue={emptyField}
            className="tw:hidden tw:sm:flex"
            testId="birthdate"
          />
        )}
        <Stat
          icon="calendar"
          label={tSupporterShow('joined_group')}
          value={joinedGroup}
          fallbackValue={emptyField}
          className="tw:hidden tw:sm:flex"
          testId="joined-group"
        />
        <Stat
          icon="clock"
          label={tSupporterShow('last_contacted')}
          value={lastContactedDate}
          fallbackValue={t('supporter_card.never_contacted')}
          className="tw:hidden tw:sm:flex"
          testId="last-contacted"
        />
        <Stat
          icon="ticket-01"
          label={tSupporterShow('rsvp_count')}
          value={String(rsvpCount)}
          fallbackValue={emptyField}
          className="tw:hidden tw:sm:flex"
          testId="rsvp-count"
        />
      </dl>

      {showAllTags && tagNodes.length > 0 && (
        <div className="tw:flex tw:items-center tw:gap-1.5" data-testid="supporter-tags">
          <Icon type="tag-01" width={20} height={20} className="tw:shrink-0 tw:text-gray-true-500" />
          <SupporterTagsList tags={tagNodes} maxVisibleTags={5} />
        </div>
      )}

      {note && (
        <div className="tw:flex tw:flex-col tw:gap-3" data-testid="background-note">
          <span className="tw:inline-flex tw:items-end tw:gap-1.5 tw:text-gray-true-500 tw:font-bold tw:text-xs tw:uppercase tw:leading-[18px]">
            <Icon type="sticker-square" width={20} height={20} className="tw:text-gray-true-500" />
            {tSupporterShow('background_note.title')}
          </span>
          <p className="tw:border-l-2 tw:border-gray-200 tw:pl-5 tw:text-sm tw:text-gray-900 tw:whitespace-pre-wrap tw:m-0 tw:line-clamp-2 tw:sm:line-clamp-none">
            {note}
          </p>
        </div>
      )}

      <div className="tw:flex tw:flex-wrap tw:gap-3 tw:pt-2 tw:sm:justify-between">
        <span className="tw:inline-flex tw:gap-3 tw:flex-wrap">
          <Button
            size="sm"
            rank="primary"
            leadingIcon="clipboard-minus"
            data-testid="supporter-log-contact"
            ariaLabel={t('supporter_card.actions.log_contact_aria', { name: firstName })}
            onClick={() => {
              openDialog({ type: 'logContact', dialogData: { id, firstName, lastName, groupId } })
            }}
          >
            {t('supporter_card.actions.log_contact', { name: firstName })}
          </Button>
          {demoSite ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    rank="secondary"
                    leadingIcon="mail-01"
                    disabled
                    data-testid="supporter-send-email"
                    ariaLabel={`${t('supporter_card.actions.send_email_aria', { name: firstName })}. ${tShared('demo_site.sending_disabled')}`}
                  >
                    {t('supporter_card.actions.send_email', { name: firstName })}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{tShared('demo_site.sending_disabled')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              size="sm"
              rank="secondary"
              leadingIcon="mail-01"
              disabled={!membership?.emailable}
              data-testid="supporter-send-email"
              ariaLabel={t('supporter_card.actions.send_email_aria', { name: firstName })}
              onClick={() => {
                openDialog({ type: 'sendEmail', dialogData: { id, firstName, lastName, groupId } })
              }}
            >
              {t('supporter_card.actions.send_email', { name: firstName })}
            </Button>
          )}
          {canSendIndividualSms && !demoSite ? (
            <Button
              size="sm"
              rank="secondary"
              leadingIcon="message-chat-square"
              disabled={!membership?.textable}
              data-testid="supporter-send-sms"
              ariaLabel={t('supporter_card.actions.send_sms_aria', { name: firstName })}
              onClick={() => {
                openDialog({ type: 'sendSms', dialogData: { id, firstName, lastName, groupId } })
              }}
            >
              {t('supporter_card.actions.send_sms', { name: firstName })}
            </Button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    rank="secondary"
                    leadingIcon="message-chat-square"
                    disabled
                    data-testid="supporter-send-sms"
                    ariaLabel={`${t('supporter_card.actions.send_sms_aria', { name: firstName })}. ${smsDisabledReason}`}
                  >
                    {t('supporter_card.actions.send_sms', { name: firstName })}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{smsDisabledReason}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {canMakePhoneCalls && !demoSite ? (
            <Button
              size="sm"
              rank="secondary"
              leadingIcon="phone-call-01"
              disabled={!membership?.callable}
              data-testid="supporter-initiate-call"
              ariaLabel={t('supporter_card.actions.call_aria', { name: firstName })}
              onClick={() => {
                openDialog({ type: 'initiateCall', dialogData: { id, firstName, lastName, groupId } })
              }}
            >
              {t('supporter_card.actions.call', { name: firstName })}
            </Button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    rank="secondary"
                    leadingIcon="phone-call-01"
                    disabled
                    data-testid="supporter-initiate-call"
                    ariaLabel={`${t('supporter_card.actions.call_aria', { name: firstName })}. ${callDisabledReason}`}
                  >
                    {t('supporter_card.actions.call', { name: firstName })}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{callDisabledReason}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>

        <span className="tw:inline-flex tw:gap-3">
          <Link
            id={`supporter-open-${id}`}
            href={supporterUrl}
            as="modalLink"
            size="sm"
            rank="secondary"
            color="primary"
            leadingIcon="external-link"
          >
            {t('supporter_card.actions.open_profile', { name: firstName })}
          </Link>
        </span>
      </div>
    </div>
  )
}
